import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchJSON, patchJSON } from 'api/fetch';

import { actions as billerActions } from 'features/biller';
import { getPayrollFeatureEnabled } from 'features/payroll/redux/selectors/payroll';

import { transformKeysToCamelcase } from 'util/transformKeysToCamelcase';
export const fetchDashboardData = createAsyncThunk(
  'payroll/fetchPayrollRuns',
  () => fetchJSON(`/payroll.json`)
);

export const fetchPayrollIneligibleEmployees = createAsyncThunk(
  'payroll/fetchPayrollIneligibleEmployees',
  (_, { getState, rejectWithValue }) => {
    const state = getState();
    const payrollIsActive = getPayrollFeatureEnabled(state, 'payroll_active');
    if (payrollIsActive) {
      return fetchJSON(`/payroll/payroll_ineligible_employees.json`);
    }

    return rejectWithValue();
  }
);

export const fetchComponents = createAsyncThunk(
  'fetchComponents',
  (_, { rejectWithValue }) =>
    fetchJSON(`/payroll/implementation/dashboard_components.json`).catch(err =>
      err.response.json().then(body => rejectWithValue(body))
    )
);

export const getMailTaxFormsData = createAsyncThunk(
  'getMailTaxFormsData',
  (_, { rejectWithValue }) =>
    fetchJSON(`/payroll/get_mail_tax_forms_data`).catch(err =>
      err.response.json().then(body => rejectWithValue(body))
    )
);

export const updateMailTaxForms = createAsyncThunk(
  'updateMailTaxForms',
  (payload, { rejectWithValue }) =>
    patchJSON('/payroll/update_mail_tax_forms', payload).catch(err =>
      err.response.json().then(body => rejectWithValue(body))
    )
);

const payrollDashboardSlice = createSlice({
  name: 'payrollDashboard',

  initialState: {
    isFetching: true,
    isComponentsLoading: true,
    payPeriodDateRange: '',
    payPeriodStartDate: '',
    payPeriodEndDate: '',
    payDate: '',
    rawPayDate: '',
    payrollDue: '',
    payrollDueColor: '',
    payrollDuePillVariant: '',
    activeLocationNames: '',
    runLocationNamesToSentence: '',
    isPayrollPastDue: false,
    isPayrollDueToday: false,
    submissionDeadlineDate: '',
    submissionDeadlineTime: '',
    submissionDeadlineCountdown: '',
    autopayrollSubmissionDeadlineTime: '',
    autopayrollSubmissionDeadlineCountdown: '',
    autopayrollSubmissionTimeOptions: [],
    primaryLocationTimeZone: '',
    showPayrollHistory: false,
    currentPayPeriodPayrollRun: null,
    payrollHistoryInitialDates: '',
    payrollHistoryCalendarPickerDateRangeOptions: '',
    isRunStandardPayrollButtonDisabled: false,
    isRunOffCyclePayrollButtonDisabled: false,
    needsReviewEmployees: [],
    isManuallyPaid: null,
    hasEligibleEmployees: false,
    isPayrollCompanyChurned: false,
    featureFlags: {
      offCyclePayrollButton: false,
      standardPayrollRunWidget: false,
      needsReviewWidget: false,
      payrollReports: false,
      manageTimeOff: false,
    },
    implementationComponents: [],
    implementationMetadata: {},
    hasFailedPreNoteValidation: false,
    showAdjustedCheckPaydaysAlert: false,
    showTaxFormsWidget: false,
    submissionDeadlineAdjustedBanner: null,
    recentFailedPaymentsPayrollRun: {},
    showFailedPaymentsRefundedModal: null,
    taxDocumentsCount: null,
    mailTaxFormsData: {},
    mailTaxFormsDataPending: false,
    billingUpdated: false,
    companyName: '',
    referralModal: { open: false, referrer: '' },
    isReferralSent: false,
    showWireFundsBanner: false,
    wireDetails: {},
    // Toast state
    showDashboardToast: false,
    dashboardToastMessage: '',
  },

  reducers: {
    showToast: (state, action) => {
      state.showDashboardToast = true;
      state.dashboardToastMessage = action.payload;
    },

    hideToast: state => {
      state.showDashboardToast = false;
    },

    setReferralModal: (state, action) => {
      state.referralModal = action.payload;
    },

    setIsReferralSent: (state, action) => {
      state.isReferralSent = action.payload;
    },

    setIsFetching: (state, action) => {
      state.isFetching = action.payload;
    },

    updateRecentFailedPaymentsPayrollRun: (state, action) => {
      state.recentFailedPaymentsPayrollRun = action.payload;
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchDashboardData.pending, state => {
      state.isFetching = true;
    });

    builder.addCase(fetchDashboardData.fulfilled, (state, action) => {
      state.payPeriodDateRange = action.payload.pay_period_date_range;
      state.payPeriodStartDate = action.payload.pay_period_start_date;
      state.payPeriodEndDate = action.payload.pay_period_end_date;
      state.payDate = action.payload.pay_date;
      state.rawPayDate = action.payload.raw_pay_date;
      state.payrollDue = action.payload.payroll_due;
      state.payrollDueColor = action.payload.payroll_due_color;
      state.payrollDuePillVariant = action.payload.payroll_due_pill_variant;
      state.activeLocationNames = action.payload.active_location_names;
      state.runLocationNamesToSentence =
        action.payload.run_location_names_to_sentence;
      state.isPayrollPastDue = action.payload.is_payroll_past_due;
      state.isPayrollDueToday = action.payload.is_payroll_due_today;
      state.isAutopayrollPastSubmission =
        action.payload.autopayroll_past_submission;
      state.submissionDeadlineDate = action.payload.submission_deadline_date;
      state.submissionDeadlineDateWithYear =
        action.payload.submission_deadline_date_with_year;
      state.submissionDeadlineTime = action.payload.submission_deadline_time;
      state.submissionDeadlineCountdown =
        action.payload.submission_deadline_countdown;
      state.autopayrollSubmissionDeadlineTime =
        action.payload.autopayroll_submission_deadline_time;
      state.reopenDeadlineDate = action.payload.reopen_deadline_date;
      state.reopenDeadlineTime = action.payload.reopen_deadline_time;
      state.autopayrollSubmissionDeadlineCountdown =
        action.payload.autopayroll_submission_deadline_countdown;
      state.autopayrollSubmissionTimeOptions =
        action.payload.autopayroll_submission_time_options;
      state.guidelineBannerDismissed =
        action.payload.guideline_banner_dismissed;
      state.primaryLocationTimeZone = action.payload.primary_location_time_zone;
      state.showPayrollHistory = action.payload.show_payroll_history;
      state.isPayrollCompanyChurned = action.payload.is_payroll_company_churned;
      state.featureFlags = transformKeysToCamelcase(
        action.payload.feature_flags
      );
      state.isRunStandardPayrollButtonDisabled =
        action.payload.block_run_standard_payroll;
      state.isRunOffCyclePayrollButtonDisabled =
        action.payload.block_run_off_cycle_payroll;
      state.companyName = action.payload.company_name;
      state.isCheckBankAccountNotCreatedOrFailed =
        action.payload.is_check_bank_account_not_created_or_failed;
      state.currentPayPeriodPayrollRun =
        action.payload.current_pay_period_payroll_run;
      state.payrollHistoryInitialDates =
        action.payload.payroll_history_initial_dates;
      state.payrollHistoryCalendarPickerDateRangeOptions =
        action.payload.payroll_history_calendar_picker_date_range_options;
      state.needsReviewEmployees = action.payload.needs_review_employees;
      state.isManuallyPaid = action.payload.is_manually_paid;
      state.hasEligibleEmployees = action.payload.has_eligible_employees;
      state.showAdjustedCheckPaydaysAlert =
        action.payload.show_adjusted_check_paydays_alert;
      state.showTaxFormsWidget = action.payload.show_tax_forms_widget;
      state.taxDocumentsCount = action.payload.tax_documents_count;
      state.submissionDeadlineAdjustedBanner =
        action.payload.submission_deadline_adjusted_banner;
      state.recentFailedPaymentsPayrollRun =
        action.payload.recent_failed_payments_payroll_run;
      state.showFailedPaymentsRefundedModal =
        action.payload.show_failed_payments_refunded_modal;
      state.showWireFundsBanner = action.payload.show_wire_funds_banner;
      state.wireDetails = action.payload.wire_details;
      state.isFetching = false;
    });

    builder.addCase(
      fetchPayrollIneligibleEmployees.fulfilled,
      (state, action) => {
        state.needsReviewEmployees = action.payload;
      }
    );

    builder.addCase(fetchPayrollIneligibleEmployees.rejected, state => {
      state.needsReviewEmployees = [];
    });

    builder.addCase(fetchComponents.pending, state => {
      state.isComponentsLoading = true;
    });

    builder.addCase(fetchComponents.fulfilled, (state, action) => {
      state.implementationComponents = action.payload.components;
      state.implementationMetadata = transformKeysToCamelcase(
        action.payload.metadata
      );
      state.isComponentsLoading = false;
    });

    builder.addCase(fetchComponents.rejected, state => {
      state.implementationComponents = [];
      state.implementationMetadata = {};
      state.isComponentsLoading = false;
    });

    builder.addCase(getMailTaxFormsData.pending, state => {
      state.mailTaxFormsDataPending = true;
    });

    builder.addCase(getMailTaxFormsData.fulfilled, (state, action) => {
      state.mailTaxFormsDataPending = false;
      state.mailTaxFormsData = action.payload;
    });

    builder.addCase(getMailTaxFormsData.rejected, state => {
      state.mailTaxFormsDataPending = false;
    });
    builder.addCase(billerActions.paymentMethodUpdated, () => {
      window.location.reload();
    });
  },
});

export const payrollDashboardReducer = payrollDashboardSlice.reducer;
export const payrollDashboardActions = payrollDashboardSlice.actions;
